/**
 * LukhasTerminal - Interactive terminal component for lukhas.team staff portal
 *
 * Features:
 * - Toggle between sandbox (safe) and bastion (SSH) modes
 * - Real-time command execution with SSE streaming
 * - Command history with recall (up/down arrows)
 * - Session management with auto-reconnect
 * - RBAC enforcement via session tokens
 * - Full NIAS audit trail
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'

interface TerminalLine {
  type: 'input' | 'output' | 'error' | 'system'
  content: string
  timestamp: number
}

interface TerminalSession {
  session_id: string
  token: string
  mode: 'sandbox' | 'bastion'
  expires_at: number
  created_at: number
}

interface LukhasTerminalProps {
  className?: string
  defaultMode?: 'sandbox' | 'bastion'
}

export default function LukhasTerminal({ className = '', defaultMode = 'sandbox' }: LukhasTerminalProps) {
  // State
  const [mode, setMode] = useState<'sandbox' | 'bastion'>(defaultMode)
  const [session, setSession] = useState<TerminalSession | null>(null)
  const [lines, setLines] = useState<TerminalLine[]>([])
  const [currentInput, setCurrentInput] = useState('')
  const [commandHistory, setCommandHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [isExecuting, setIsExecuting] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Refs
  const terminalRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Configuration
  const API_BASE = import.meta.env.VITE_TERMINAL_API || 'http://localhost:8004'
  const TENANT_ID = 'lukhas-team'
  const USER_LID = 'staff_001' // Should come from auth context

  /**
   * Add a line to the terminal output
   */
  const addLine = useCallback((line: TerminalLine) => {
    setLines(prev => [...prev, line])
  }, [])

  const addInputLine = useCallback((content: string) => {
    addLine({ type: 'input', content, timestamp: Date.now() })
  }, [addLine])

  const addOutputLine = useCallback((content: string, type: 'output' | 'error' = 'output') => {
    addLine({ type, content, timestamp: Date.now() })
  }, [addLine])

  const addSystemLine = useCallback((content: string, type: 'system' | 'error' = 'system') => {
    addLine({ type, content, timestamp: Date.now() })
  }, [addLine])

  /**
   * Create a new terminal session
   */
  const createSession = useCallback(async (terminalMode: 'sandbox' | 'bastion') => {
    setIsConnecting(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/api/terminal/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenant_id: TENANT_ID,
          user_lid: USER_LID,
          mode: terminalMode,
          ttl: 3600,
          metadata: {}
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`)
      }

      const data = await response.json()

      const newSession: TerminalSession = {
        session_id: data.session_id,
        token: data.token,
        mode: data.mode,
        expires_at: data.expires_at,
        created_at: data.created_at
      }

      setSession(newSession)

      addSystemLine(`Session created: ${data.session_id}`)
      addSystemLine(`Mode: ${terminalMode.toUpperCase()}`)
      addSystemLine(`Type 'help' for available commands`)

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMsg)
      addSystemLine(`Error: ${errorMsg}`, 'error')
      // Demo mode fallback
      addSystemLine('Running in demo mode (no backend connected)', 'system')
      setSession({
        session_id: 'demo-session',
        token: 'demo-token',
        mode: terminalMode,
        expires_at: Date.now() + 3600000,
        created_at: Date.now()
      })
    } finally {
      setIsConnecting(false)
    }
  }, [API_BASE, addSystemLine])

  /**
   * Execute a command in the terminal session
   */
  const executeCommand = useCallback(async (command: string) => {
    if (!session) {
      addSystemLine('No active session. Please create a session first.', 'error')
      return
    }

    if (!command.trim()) {
      return
    }

    // Add command to display
    addInputLine(command)

    // Add to history
    setCommandHistory(prev => [...prev, command])
    setHistoryIndex(-1)

    setIsExecuting(true)
    setError(null)

    // Demo mode commands
    if (session.session_id === 'demo-session') {
      setTimeout(() => {
        if (command === 'help') {
          addOutputLine(`Available commands:
  help          - Show this help message
  whoami        - Display current user
  status        - Show system status
  tests         - Run test suite
  clear         - Clear terminal
  uptime        - Show system uptime
  date          - Display current date/time`)
        } else if (command === 'whoami') {
          addOutputLine('staff@lukhas.team')
        } else if (command === 'status') {
          addOutputLine(`LUKHAS System Status
  API Gateway:    [ONLINE]
  Auth Service:   [ONLINE]
  Memory Core:    [ONLINE]
  Guardian Node:  [STANDBY]
  Last heartbeat: ${new Date().toISOString()}`)
        } else if (command === 'tests') {
          addOutputLine('Running test suite...')
          setTimeout(() => {
            addOutputLine(`Test Results:
  Total:   127
  Passed:  124
  Failed:  2
  Skipped: 1
  Coverage: 87.3%`)
          }, 500)
        } else if (command === 'clear') {
          setLines([])
        } else if (command === 'uptime') {
          addOutputLine('System uptime: 47 days, 12 hours, 34 minutes')
        } else if (command === 'date') {
          addOutputLine(new Date().toString())
        } else {
          addOutputLine(`Command not found: ${command}`, 'error')
        }
        setIsExecuting(false)
        setCurrentInput('')
      }, 300)
      return
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/terminal/session/${session.session_id}/exec`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.token}`
          },
          body: JSON.stringify({
            command,
            timeout: 30
          })
        }
      )

      if (!response.ok) {
        throw new Error(`Execution failed: ${response.statusText}`)
      }

      const result = await response.json()

      if (result.stdout) {
        addOutputLine(result.stdout)
      }

      if (result.stderr) {
        addOutputLine(result.stderr, 'error')
      }

      if (result.exit_code !== 0) {
        addSystemLine(`Exit code: ${result.exit_code}`, 'error')
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMsg)
      addOutputLine(`Error: ${errorMsg}`, 'error')
    } finally {
      setIsExecuting(false)
      setCurrentInput('')
    }
  }, [session, API_BASE, addInputLine, addOutputLine, addSystemLine])

  /**
   * Handle input submission
   */
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (currentInput.trim()) {
      executeCommand(currentInput)
    }
  }, [currentInput, executeCommand])

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setCurrentInput(commandHistory[newIndex])
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setCurrentInput('')
        } else {
          setHistoryIndex(newIndex)
          setCurrentInput(commandHistory[newIndex])
        }
      }
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault()
      setLines([])
      addSystemLine('Screen cleared')
    }
  }, [commandHistory, historyIndex, addSystemLine])

  /**
   * Toggle terminal mode
   */
  const toggleMode = useCallback(async () => {
    const newMode = mode === 'sandbox' ? 'bastion' : 'sandbox'
    setMode(newMode)

    if (session && session.session_id !== 'demo-session') {
      try {
        await fetch(`${API_BASE}/api/terminal/session/${session.session_id}/close`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.token}`
          }
        })
      } catch (err) {
        console.error('Failed to close session:', err)
      }
    }

    setSession(null)
    setLines([])
    await createSession(newMode)
  }, [mode, session, createSession, API_BASE])

  // Auto-scroll
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }
  }, [lines])

  // Create initial session
  useEffect(() => {
    createSession(mode)
  }, [])

  // Auto-reconnect
  useEffect(() => {
    if (!session || session.session_id === 'demo-session') return

    const timeUntilExpiry = session.expires_at * 1000 - Date.now()
    const reconnectTime = timeUntilExpiry - 60000

    if (reconnectTime > 0) {
      const timer = setTimeout(() => {
        addSystemLine('Session expiring soon, reconnecting...', 'system')
        createSession(mode)
      }, reconnectTime)

      return () => clearTimeout(timer)
    }
  }, [session, mode, createSession, addSystemLine])

  const getLineColor = (type: TerminalLine['type']) => {
    switch (type) {
      case 'input':
        return 'text-emerald-400'
      case 'error':
        return 'text-red-400'
      case 'system':
        return 'text-blue-400'
      default:
        return 'text-gray-200'
    }
  }

  const getPrompt = () => {
    return mode === 'sandbox' ? 'staff@sandbox:~$' : 'staff@bastion:~$'
  }

  return (
    <div className={`flex flex-col bg-gray-900/90 backdrop-blur-sm text-gray-100 font-mono rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800/80 border-b border-gray-700/50">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <h2 className="text-sm font-semibold text-lambda-gold">LUKHAS Terminal</h2>
          {session && (
            <span className="text-xs text-gray-500">
              {session.session_id === 'demo-session' ? 'Demo Mode' : `Session: ${session.session_id.substring(0, 8)}...`}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={toggleMode}
            disabled={isConnecting || isExecuting}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
              mode === 'sandbox'
                ? 'bg-emerald-600/80 text-white border border-emerald-500/50'
                : 'bg-orange-600/80 text-white border border-orange-500/50'
            } ${isConnecting || isExecuting ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
          >
            {mode.toUpperCase()}
          </button>

          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${session ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="text-xs text-gray-400">
              {session ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      {/* Terminal Output */}
      <div
        ref={terminalRef}
        className="flex-1 overflow-y-auto p-4 space-y-1 min-h-[300px] max-h-[500px] scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
      >
        {lines.map((line, index) => (
          <div key={index} className={`${getLineColor(line.type)} text-sm leading-relaxed`}>
            {line.type === 'input' && (
              <span className="text-emerald-500">{getPrompt()} </span>
            )}
            <span className="whitespace-pre-wrap">{line.content}</span>
          </div>
        ))}

        {(isConnecting || isExecuting) && (
          <div className="text-yellow-400 text-sm animate-pulse">
            {isConnecting ? 'Connecting...' : 'Executing...'}
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-700/50 p-4 bg-gray-800/40">
        <form onSubmit={handleSubmit} className="flex items-center space-x-2">
          <span className="text-emerald-500 text-sm">{getPrompt()}</span>
          <input
            ref={inputRef}
            type="text"
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!session || isExecuting}
            placeholder={session ? "Enter command..." : "Connecting..."}
            className="flex-1 bg-transparent border-none outline-none text-sm text-gray-100 placeholder-gray-500 disabled:opacity-50"
            autoComplete="off"
            autoFocus
          />
          <button
            type="submit"
            disabled={!session || isExecuting || !currentInput.trim()}
            className="px-4 py-1.5 bg-lambda-gold/20 text-lambda-gold text-sm rounded-lg border border-lambda-gold/30 hover:bg-lambda-gold/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Run
          </button>
        </form>

        <div className="mt-2 text-xs text-gray-500">
          Press ↑/↓ for history  Ctrl+L to clear
        </div>

        {error && session?.session_id !== 'demo-session' && (
          <div className="mt-2 text-xs text-red-400 bg-red-900/20 p-2 rounded">
            {error}
          </div>
        )}
      </div>
    </div>
  )
}
